import React, { useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { Search, ChevronRight } from 'lucide-react';

export interface SearchableItem {
  id: string | number;
  type: string;
  name: string;
  code?: string;
  description?: string;
  page: string;
  icon?: React.ComponentType<{ className?: string }>;
  meta?: string;
}

export interface SearchCategory {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

export interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (page: string) => void;
  items: SearchableItem[];
  categories: SearchCategory[];
}

export function GlobalSearch({
  open,
  onClose,
  onNavigate,
  items,
  categories,
}: GlobalSearchProps) {
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    if (!open) { setQuery(''); setSelectedCategory('all'); }
  }, [open]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const q = query.toLowerCase();
  const filtered = items.filter((item) => {
    const matchesQuery =
      !q ||
      item.name.toLowerCase().includes(q) ||
      item.code?.toLowerCase().includes(q) ||
      item.description?.toLowerCase().includes(q);
    const matchesCat = selectedCategory === 'all' || item.type === selectedCategory;
    return matchesQuery && matchesCat;
  });

  const getCategoryColor = (type: string) =>
    categories.find((c) => c.id === type)?.color ?? 'bg-gray-100 text-gray-600';

  const getCategoryIcon = (type: string) =>
    categories.find((c) => c.id === type)?.icon ?? Search;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search sources, tables, views, clients..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 text-lg outline-none bg-transparent text-gray-900 dark:text-white placeholder-gray-400"
            />
            <kbd className="px-2 py-1 text-xs font-mono bg-gray-100 dark:bg-gray-800 text-gray-500 rounded">
              ESC
            </kbd>
          </div>

          {/* Category filters */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 dark:border-gray-800 overflow-x-auto">
            <button
              onClick={() => setSelectedCategory('all')}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors',
                selectedCategory === 'all'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
              )}
            >
              <Search className="w-3.5 h-3.5" />
              All
            </button>
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors',
                    selectedCategory === cat.id
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-12 text-center">
                <Search className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <p className="text-gray-500">No results found</p>
              </div>
            ) : (
              <div className="py-2">
                {filtered.slice(0, 12).map((item) => {
                  const Icon = getCategoryIcon(item.type);
                  return (
                    <button
                      key={item.id}
                      onClick={() => { onNavigate(item.page); onClose(); }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 text-left transition-colors"
                    >
                      <div
                        className={clsx(
                          'w-10 h-10 rounded-lg flex items-center justify-center',
                          getCategoryColor(item.type)
                        )}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {item.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {item.code && <span className="font-mono">{item.code}</span>}
                          {item.meta && <span className="ml-2">• {item.meta}</span>}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded text-[10px]">↑↓</kbd> Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded text-[10px]">↵</kbd> Open
              </span>
            </div>
            <span>{filtered.length} results</span>
          </div>
        </div>
      </div>
    </>
  );
}
